import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const WebSocketContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

export function WebSocketProvider({ children }) {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const [newMessages, setNewMessages] = useState([]);
  const [liveNotifications, setLiveNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const { token, user } = useAuth();

  // Add a live notification
  const addLiveNotification = useCallback((notification) => {
    setLiveNotifications(prev => [notification, ...prev].slice(0, 50)); // Keep last 50
    setUnreadNotificationCount(prev => prev + 1);
  }, []);

  // Clear unread count
  const clearUnreadNotifications = useCallback(() => {
    setUnreadNotificationCount(0);
  }, []);

  const connect = useCallback(() => {
    if (!token || !WS_URL) return;

    try {
      const ws = new WebSocket(`${WS_URL}/ws/${token}`);

      ws.onopen = () => {
        setIsConnected(true);
        
        // Start ping interval
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          switch (data.type) {
            case 'new_message':
              setNewMessages(prev => [...prev, data.message]);
              // Show toast notification
              if (data.message.sender_id !== user?.id) {
                toast.info(`New message from ${data.message.sender_username}`, {
                  description: data.message.text?.substring(0, 50) || 'Voice message',
                  action: {
                    label: 'View',
                    onClick: () => window.location.href = `/messages/${data.message.sender_id}`
                  }
                });
                addLiveNotification({
                  id: Date.now(),
                  type: 'message',
                  title: `Message from ${data.message.sender_username}`,
                  body: data.message.text?.substring(0, 100) || 'Voice message',
                  link: `/messages/${data.message.sender_id}`,
                  timestamp: new Date().toISOString()
                });
              }
              break;
            
            case 'typing':
              setTypingUsers(prev => ({
                ...prev,
                [data.user_id]: data.is_typing ? data.username : null
              }));
              break;
            
            case 'user_status':
              if (data.status === 'online') {
                setOnlineUsers(prev => [...new Set([...prev, data.user_id])]);
              } else {
                setOnlineUsers(prev => prev.filter(id => id !== data.user_id));
              }
              break;
            
            case 'read_receipt':
              // Handle read receipt - update message state
              window.dispatchEvent(new CustomEvent('message_read', { detail: data }));
              break;
            
            case 'notification':
              // iter_407 — backend sends two shapes:
              //   {type:'notification', title, body, ...}            (flat)
              //   {type:'notification', notification: {title, body}}  (wrapped — used by services/notification_service.py)
              // Coerce both into one canonical payload before showing toast.
              const nDoc = data.notification && typeof data.notification === 'object'
                ? { ...data.notification, ...data, ...data.notification }
                : data;

              addLiveNotification({
                id: nDoc.id || Date.now(),
                type: nDoc.notification_type || nDoc.type || 'general',
                title: nDoc.title,
                body: nDoc.body,
                link: nDoc.link,
                timestamp: nDoc.created_at || new Date().toISOString()
              });

              // Show toast based on notification type
              const notifIcons = {
                follow: '👤',
                like: '❤️',
                purchase: '💰',
                comment: '💬',
                mention: '@',
                upload: '🎵',
                approval: '✅',
                rejection: '❌',
                review_paid: '🎉'
              };
              const _ntype = nDoc.notification_type || nDoc.type;
              const icon = notifIcons[_ntype] || '🔔';

              toast.info(`${icon} ${nDoc.title || 'New notification'}`, {
                description: nDoc.body?.substring(0, 120),
                action: nDoc.link ? {
                  label: 'View',
                  onClick: () => window.location.href = nDoc.link
                } : undefined
              });
              break;
            
            case 'push_notification':
              // Show browser notification
              if (Notification.permission === 'granted') {
                new Notification(data.title, {
                  body: data.body,
                  icon: '/logo192.png',
                  data: data.data
                });
              }
              addLiveNotification({
                id: Date.now(),
                type: 'push',
                title: data.title,
                body: data.body,
                timestamp: new Date().toISOString()
              });
              break;
            
            case 'pong':
              // Server responded to ping
              break;
            
            default:
              break;
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        setSocket(null);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        
        // Reconnect after 3 seconds (unless intentional close)
        if (event.code !== 1000 && token) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 3000);
        }
      };

      setSocket(ws);
    } catch (error) {
      console.error('Error creating WebSocket:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: mount-only fetch / stable closure
  }, [token, user?.id]);

  useEffect(() => {
    if (token) {
      connect();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (socket) {
        socket.close(1000);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: mount-only fetch / stable closure
  }, [token, connect]);

  const sendTypingIndicator = useCallback((recipientId, isTyping) => {
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({
        type: 'typing',
        recipient_id: recipientId,
        is_typing: isTyping
      }));
    }
  }, [socket]);

  const sendReadReceipt = useCallback((messageId, senderId) => {
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({
        type: 'read_receipt',
        message_id: messageId,
        sender_id: senderId
      }));
    }
  }, [socket]);

  const clearNewMessage = useCallback((messageId) => {
    setNewMessages(prev => prev.filter(m => m.id !== messageId));
  }, []);

  const isUserOnline = useCallback((userId) => {
    return onlineUsers.includes(userId);
  }, [onlineUsers]);

  const isUserTyping = useCallback((userId) => {
    return !!typingUsers[userId];
  }, [typingUsers]);

  return (
    <WebSocketContext.Provider value={{
      isConnected,
      onlineUsers,
      typingUsers,
      newMessages,
      liveNotifications,
      unreadNotificationCount,
      sendTypingIndicator,
      sendReadReceipt,
      clearNewMessage,
      clearUnreadNotifications,
      isUserOnline,
      isUserTyping
    }}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}
